import { useEffect, useRef, useState } from "react";
import { Prospects as ProspectsAPI, AI, Messages, Campaigns as CampaignsAPI, FollowUps, Users } from "@/lib/api";
import { useTeam } from "@/lib/team";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, UploadSimple, Globe, Sparkle, PaperPlaneRight, Trash, MagnifyingGlass, EnvelopeSimple, ChatCircleText,
  Lightbulb, ArrowUUpLeft, Check, LinkedinLogo
} from "@phosphor-icons/react";

const statusColors = {
  new: "bg-neutral-100 text-neutral-700 border-neutral-300",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  replied: "bg-amber-50 text-amber-700 border-amber-200",
  booked: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-purple-50 text-purple-700 border-purple-200",
  unqualified: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function ProspectsPage() {
  const { users, currentUser } = useTeam();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [linkedInOpen, setLinkedInOpen] = useState(false);
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [linkedInPaste, setLinkedInPaste] = useState("");
  const [linkedInNeedsPaste, setLinkedInNeedsPaste] = useState(false);
  const [linkedInBusy, setLinkedInBusy] = useState(false);
  const [draftFor, setDraftFor] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (ownerFilter === "mine" && currentUser) params.assigned_to = currentUser.id;
    else if (ownerFilter === "unassigned") params.assigned_to = "unassigned";
    else if (ownerFilter !== "all" && ownerFilter !== "mine") params.assigned_to = ownerFilter;
    if (q) params.q = q;
    const data = await ProspectsAPI.list(params);
    setList(data);
  };

  useEffect(() => { load(); }, [filter, ownerFilter, currentUser?.id]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const res = await ProspectsAPI.importCsv(f);
      toast.success(`Imported ${res.imported} prospects`);
      load();
    } catch (err) { toast.error("CSV import failed"); }
    e.target.value = "";
  };

  const removeProspect = async (id) => {
    if (!confirm("Delete this prospect?")) return;
    await ProspectsAPI.remove(id);
    toast.success("Prospect deleted");
    load();
  };

  const enrichProspect = async (p) => {
    if (!p.website) return toast.error("Add a website URL first");
    toast.info("Enriching from website...");
    try {
      const res = await ProspectsAPI.enrich(p.id);
      toast.success("Insights added");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrichment failed");
    }
  };

  const markReplied = async (p) => {
    if (!confirm(`Mark ${p.business_name} as replied? Queued follow-ups will be cancelled.`)) return;
    try {
      await ProspectsAPI.markReplied(p.id);
      toast.success("Marked replied");
      load();
    } catch { toast.error("Failed"); }
  };

  const assign = async (pid, uid) => {
    await Users.assign(pid, uid);
    toast.success("Assigned");
    load();
  };

  const userById = (id) => users.find(u => u.id === id);

  const resetLinkedIn = () => {
    setLinkedInOpen(false);
    setLinkedInUrl("");
    setLinkedInPaste("");
    setLinkedInNeedsPaste(false);
    setLinkedInBusy(false);
  };

  const importLinkedIn = async () => {
    if (!linkedInUrl.trim()) return toast.error("Paste a LinkedIn URL");
    if (!/linkedin\.com\//i.test(linkedInUrl)) return toast.error("URL must be a linkedin.com page");
    setLinkedInBusy(true);
    try {
      const res = await ProspectsAPI.importLinkedIn(linkedInUrl.trim(), linkedInPaste.trim() || undefined);
      if (res.needs_paste) {
        setLinkedInNeedsPaste(true);
        toast.info("LinkedIn blocked the fetch. Paste the profile text below.");
      } else if (res.ok) {
        toast.success(`Imported ${res.prospect.business_name}${res.fetched ? "" : " (from your paste)"}`);
        resetLinkedIn();
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "LinkedIn import failed");
    } finally {
      setLinkedInBusy(false);
    }
  };

  return (
    <div data-testid="prospects-page">
      <PageHeader
        overline="Discovery · Outreach"
        title="Prospects"
        description="Discover, import, or add prospects. Then draft & ship personalized outreach."
        testId="prospects-header"
        actions={
          <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="csv-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-csv-btn">
              <UploadSimple size={16} className="mr-2" /> Import CSV
            </Button>
            <Button variant="outline" onClick={() => setScrapeOpen(true)} data-testid="scrape-btn">
              <Globe size={16} className="mr-2" /> Scrape Area
            </Button>
            <Button variant="outline" onClick={() => setLinkedInOpen(true)} data-testid="linkedin-btn">
              <LinkedinLogo size={16} className="mr-2" /> LinkedIn
            </Button>
            <Button onClick={() => setAddOpen(true)} className="bg-neutral-900 hover:bg-black text-white" data-testid="add-prospect-btn">
              <Plus size={16} className="mr-2" /> Add Prospect
            </Button>
          </>
        }
      />

      <div className="p-10 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email, city..." className="pl-9 bg-white" data-testid="search-input" />
          </div>
          {users.length > 0 && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-44 bg-white" data-testid="owner-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                <SelectItem value="mine">Mine</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="status-tabs">
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="new" data-testid="tab-new">New</TabsTrigger>
              <TabsTrigger value="contacted" data-testid="tab-contacted">Contacted</TabsTrigger>
              <TabsTrigger value="replied" data-testid="tab-replied">Replied</TabsTrigger>
              <TabsTrigger value="booked" data-testid="tab-booked">Booked</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="rounded-md border-neutral-200 overflow-hidden" data-testid="prospects-table-card">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-neutral-200 bg-neutral-50">
            <div className="overline col-span-4">Business</div>
            <div className="overline col-span-2">Contact</div>
            <div className="overline col-span-2">Location</div>
            <div className="overline col-span-1">Owner</div>
            <div className="overline col-span-1">Status</div>
            <div className="overline col-span-2 text-right">Actions</div>
          </div>
          <div className="stagger-in">
            {list.length === 0 && (
              <div className="py-16 text-center text-sm text-neutral-500">
                No prospects yet. Add one, import a CSV, or scrape an area to get started.
              </div>
            )}
            {list.map(p => {
              const owner = userById(p.assigned_to);
              return (
              <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-neutral-100 row-hover items-center" data-testid={`row-${p.id}`}>
                <div className="col-span-4">
                  <div className="text-sm font-medium">{p.business_name}</div>
                  <div className="text-xs text-neutral-500 font-mono">{p.industry || "—"} · via {p.source}</div>
                </div>
                <div className="col-span-2 text-xs">
                  <div>{p.contact_name || "—"}</div>
                  <div className="text-neutral-500">{p.email || p.phone || ""}</div>
                </div>
                <div className="col-span-2 text-xs text-neutral-600">{p.city || p.address || "—"}</div>
                <div className="col-span-1">
                  {users.length > 0 ? (
                    <Select value={p.assigned_to || "none"} onValueChange={(v) => assign(p.id, v === "none" ? null : v)}>
                      <SelectTrigger className="h-8 px-2 border-neutral-200 bg-transparent w-full" data-testid={`owner-${p.id}`}>
                        {owner ? (
                          <div className="w-6 h-6 rounded-full grid place-items-center text-white text-[9px] font-mono" style={{ background: owner.color }} title={owner.name}>
                            {owner.initials}
                          </div>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <span className="text-xs text-neutral-400">—</span>}
                </div>
                <div className="col-span-1">
                  <Badge variant="outline" className={`${statusColors[p.status]} uppercase text-[10px] tracking-widest font-mono`}>{p.status}</Badge>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => enrichProspect(p)} data-testid={`enrich-btn-${p.id}`} title="Enrich from website">
                    <Lightbulb size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDraftFor(p)} data-testid={`draft-btn-${p.id}`} title="AI draft">
                    <Sparkle size={16} />
                  </Button>
                  {p.status === "contacted" && (
                    <Button size="sm" variant="ghost" onClick={() => markReplied(p)} data-testid={`replied-btn-${p.id}`} title="Mark replied">
                      <ArrowUUpLeft size={16} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => removeProspect(p.id)} data-testid={`delete-btn-${p.id}`} title="Delete">
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            );})}
          </div>
        </Card>
      </div>

      <AddProspectDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <ScrapeDialog open={scrapeOpen} onOpenChange={setScrapeOpen} onDone={load} />
      <DraftDialog prospect={draftFor} onClose={() => setDraftFor(null)} onSent={load} />

      <Dialog open={linkedInOpen} onOpenChange={(v) => (v ? setLinkedInOpen(true) : resetLinkedIn())}>
        <DialogContent className="max-w-lg" data-testid="linkedin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Import from LinkedIn</DialogTitle>
            <DialogDescription>
              Paste a LinkedIn profile or company URL. We'll try to auto-fetch it; if LinkedIn blocks us, paste the profile text and Claude will parse it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>LinkedIn URL</Label>
              <Input
                value={linkedInUrl}
                onChange={e => setLinkedInUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/john-doe/"
                data-testid="linkedin-url-input"
              />
            </div>
            {linkedInNeedsPaste && (
              <div className="space-y-2 p-3 border border-amber-200 bg-amber-50 rounded" data-testid="linkedin-paste-fallback">
                <div className="text-xs text-amber-900">
                  LinkedIn blocked the auto-fetch. Open the profile in your browser, select all the visible text, and paste it below. Claude will pull out the fields.
                </div>
                <Textarea
                  rows={8}
                  value={linkedInPaste}
                  onChange={e => setLinkedInPaste(e.target.value)}
                  placeholder="Paste the LinkedIn profile text here..."
                  data-testid="linkedin-paste-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetLinkedIn} data-testid="linkedin-cancel-btn">Cancel</Button>
            <Button
              className="bg-neutral-900 hover:bg-black text-white"
              onClick={importLinkedIn}
              disabled={linkedInBusy}
              data-testid="linkedin-import-btn"
            >
              <LinkedinLogo size={16} className="mr-2" />
              {linkedInBusy ? "Importing..." : linkedInNeedsPaste ? "Parse pasted text" : "Fetch & import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddProspectDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ business_name: "", contact_name: "", email: "", phone: "", website: "", address: "", city: "", industry: "", notes: "" });
  const submit = async () => {
    if (!f.business_name) return toast.error("Business name required");
    await ProspectsAPI.create(f);
    toast.success("Prospect added");
    setF({ business_name: "", contact_name: "", email: "", phone: "", website: "", address: "", city: "", industry: "", notes: "" });
    onOpenChange(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-prospect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add prospect</DialogTitle>
          <DialogDescription>Add a single lead manually. All fields except business name are optional.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Business name *</Label><Input value={f.business_name} onChange={e => setF({...f, business_name: e.target.value})} data-testid="input-business-name" /></div>
          <div><Label>Contact name</Label><Input value={f.contact_name} onChange={e => setF({...f, contact_name: e.target.value})} data-testid="input-contact-name" /></div>
          <div><Label>Industry</Label><Input value={f.industry} onChange={e => setF({...f, industry: e.target.value})} data-testid="input-industry" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={e => setF({...f, email: e.target.value})} data-testid="input-email" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF({...f, phone: e.target.value})} data-testid="input-phone" /></div>
          <div><Label>Website</Label><Input value={f.website} onChange={e => setF({...f, website: e.target.value})} data-testid="input-website" /></div>
          <div><Label>City</Label><Input value={f.city} onChange={e => setF({...f, city: e.target.value})} data-testid="input-city" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} data-testid="input-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-add-btn">Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} data-testid="save-prospect-btn">Save prospect</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScrapeDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ location: "", industry: "restaurant", radius_km: 10, limit: 25 });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.location || !f.industry) return toast.error("Location & industry required");
    setLoading(true);
    try {
      const res = await ProspectsAPI.scrape(f);
      toast.success(`Found ${res.found} new prospects`);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scrape failed");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="scrape-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Scrape an area</DialogTitle>
          <DialogDescription>Discover local businesses via OpenStreetMap. Free, no API key needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Location</Label>
            <Input value={f.location} onChange={e => setF({...f, location: e.target.value})} placeholder="San Francisco, CA" data-testid="scrape-location" />
          </div>
          <div>
            <Label>Industry</Label>
            <Select value={f.industry} onValueChange={v => setF({...f, industry: v})}>
              <SelectTrigger data-testid="scrape-industry"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["restaurant","cafe","bar","gym","dentist","doctor","pharmacy","hotel","hairdresser","beauty","lawyer","accountant","real_estate","car_repair","bakery","clothing","supermarket","florist","veterinary"].map(x => (
                  <SelectItem key={x} value={x}>{x.replace("_"," ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Radius (km)</Label>
              <Input type="number" value={f.radius_km} onChange={e => setF({...f, radius_km: Number(e.target.value)})} data-testid="scrape-radius" />
            </div>
            <div>
              <Label>Limit</Label>
              <Input type="number" value={f.limit} onChange={e => setF({...f, limit: Number(e.target.value)})} data-testid="scrape-limit" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} disabled={loading} data-testid="start-scrape-btn">
            {loading ? "Scraping..." : "Start scrape"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftDialog({ prospect, onClose, onSent }) {
  const [channel, setChannel] = useState("email");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [loading, setLoading] = useState(false);
  const [tone, setTone] = useState("friendly professional");
  const [goal, setGoal] = useState("book a 15-minute discovery call");
  const [offer, setOffer] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");

  useEffect(() => {
    if (prospect) {
      setDraft({ subject: "", body: "" });
      CampaignsAPI.list().then(setCampaigns);
    }
  }, [prospect]);

  const generate = async () => {
    if (!prospect) return;
    setLoading(true);
    try {
      const res = await AI.draft({
        prospect_id: prospect.id,
        channel,
        tone,
        goal,
        offer: offer || undefined,
        sender_name: "Alex",
        sender_company: "Prospexus",
      });
      setDraft({ subject: res.subject || "", body: res.body || "" });
      toast.success("Draft generated by Claude Sonnet 5");
    } catch { toast.error("Draft failed"); }
    finally { setLoading(false); }
  };

  const sendIt = async () => {
    if (!prospect) return;
    if (channel === "email" && !prospect.email) return toast.error("Prospect has no email");
    if (channel === "sms" && !prospect.phone) return toast.error("Prospect has no phone");
    try {
      const m = await Messages.create({
        prospect_id: prospect.id,
        channel,
        subject: draft.subject || null,
        body: draft.body,
        campaign_id: selectedCampaign || null,
      });
      const res = await Messages.send(m.id);
      if (res.mailto_url) {
        window.open(res.mailto_url, "_blank");
        toast.success("Opening your email client...");
      } else if (res.sent_via === "twilio") {
        toast.success("SMS sent via Twilio");
      } else {
        toast.success("Message queued (mock — add Twilio creds to send real SMS)");
      }
      if (selectedCampaign) {
        await FollowUps.schedule(prospect.id, selectedCampaign).catch(() => {});
      }
      onSent();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  if (!prospect) return null;
  return (
    <Dialog open={!!prospect} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Reach out to {prospect.business_name}</DialogTitle>
          <DialogDescription>AI-drafted with Claude Sonnet 5. Edit before sending.</DialogDescription>
        </DialogHeader>

        {prospect.insights && (
          <div className="p-3 border border-amber-200 bg-amber-50 rounded text-xs" data-testid="prospect-insights">
            <div className="overline mb-1">Website insights</div>
            <pre className="whitespace-pre-wrap font-sans text-neutral-800">{prospect.insights}</pre>
          </div>
        )}

        <Tabs value={channel} onValueChange={setChannel}>
          <TabsList data-testid="channel-tabs">
            <TabsTrigger value="email" data-testid="channel-email"><EnvelopeSimple size={14} className="mr-2" />Email</TabsTrigger>
            <TabsTrigger value="sms" data-testid="channel-sms"><ChatCircleText size={14} className="mr-2" />SMS</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-2 gap-4">
          <div><Label>Tone</Label><Input value={tone} onChange={e => setTone(e.target.value)} data-testid="input-tone" /></div>
          <div><Label>Goal</Label><Input value={goal} onChange={e => setGoal(e.target.value)} data-testid="input-goal" /></div>
          <div className="col-span-2"><Label>Offer / hook</Label><Input value={offer} onChange={e => setOffer(e.target.value)} placeholder="e.g. free 30-day pilot" data-testid="input-offer" /></div>
        </div>

        <Button variant="outline" onClick={generate} disabled={loading} data-testid="generate-btn">
          <Sparkle size={16} className="mr-2" /> {loading ? "Drafting..." : "Generate with Claude Sonnet 5"}
        </Button>

        {channel === "email" && (
          <div>
            <Label>Subject</Label>
            <Input value={draft.subject} onChange={e => setDraft({...draft, subject: e.target.value})} data-testid="draft-subject" />
          </div>
        )}
        <div>
          <Label>Body</Label>
          <Textarea rows={7} value={draft.body} onChange={e => setDraft({...draft, body: e.target.value})} data-testid="draft-body" />
        </div>

        {campaigns.length > 0 && (
          <div>
            <Label>Attach follow-up sequence (optional)</Label>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger data-testid="campaign-select"><SelectValue placeholder="No sequence" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No sequence</SelectItem>
                {campaigns.filter(c => c.channel === channel).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name} ({c.follow_ups?.length || 0} follow-ups)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={sendIt} disabled={!draft.body} data-testid="send-message-btn">
            <PaperPlaneRight size={16} className="mr-2" /> {channel === "email" ? "Send via my mail client" : "Send SMS"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
